import { hideOthers, Undo } from "aria-hidden";
import {
  ButtonHTMLAttributes,
  createContext,
  DetailedHTMLProps,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { isUndefined } from "../../../utils/is";
import QFocusTrap from "../FocusTrapQuantum";
import ReactDomPortal, { PortalProps } from "../PortalQuantum/index";
import { useDialogRootContext } from "./hooks";

type DialogRootContextValueType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentNode?: HTMLDivElement;
  onContentNodeChange: (node?: HTMLDivElement) => void;
  trigger?: HTMLButtonElement;
  onTriggerChange: (trigger?: HTMLButtonElement) => void;
  destroyDialogWhenEscapePress: boolean;
  onEscapeKeyDown?: () => void;
};

export const DialogRootContext = createContext<
  DialogRootContextValueType | undefined
>(undefined);

const { Provider: DialogRootProvider } = DialogRootContext;

interface DialogRootProps {
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  destroyDialogWhenEscapePress?: boolean;
  onEscapeKeyDown?: () => void;
}

const QDialogRoot: React.FC<DialogRootProps> = (props) => {
  const [unControlledOpen, setUnControlledOpen] = useState<boolean>(false);
  const [contentNode, setContentNode] = useState<HTMLDivElement>();
  const [trigger, setTrigger] = useState<HTMLButtonElement>();
  const open = isUndefined(props.open) ? unControlledOpen : props.open;
  const setOpen = isUndefined(props.onOpenChange)
    ? setUnControlledOpen
    : props.onOpenChange;

  return (
    <DialogRootProvider
      value={{
        open,
        onOpenChange: setOpen,
        contentNode,
        onContentNodeChange: setContentNode,
        trigger,
        onTriggerChange: setTrigger,
        destroyDialogWhenEscapePress: !!props.destroyDialogWhenEscapePress,
        onEscapeKeyDown: props.onEscapeKeyDown,
      }}
    >
      {props.children}
    </DialogRootProvider>
  );
};

interface DialogTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {}

const QDialogTrigger = forwardRef<HTMLButtonElement, DialogTriggerProps>(
  (props, ref) => {
    const context = useDialogRootContext();

    const toggle = () => {
      context.onOpenChange(!context.open);
    };

    return (
      <button
        type="button"
        ref={(node) => {
          if (!node) return;
          context.onTriggerChange(node);

          if (ref) {
            if (typeof ref === "function") {
              ref(node);
            } else {
              ref.current = node;
            }
          }
        }}
        {...props}
        onPointerUp={(e) => {
          toggle();
          props.onPointerUp?.(e);
        }}
      />
    );
  },
);

interface DialogPortalProps extends PortalProps {}

const QDialogPortal = (props: DialogPortalProps) => {
  return <ReactDomPortal {...props} />;
};

interface DialogOverlayProps extends HTMLAttributes<HTMLDivElement> {}

const QDialogOverlay = forwardRef<HTMLDivElement, DialogOverlayProps>(
  (props, ref) => {
    const context = useDialogRootContext();

    if (!context.open) return null;

    return (
      <div
        onClick={() => context.onOpenChange(false)}
        aria-hidden
        data-state={context.open ? "open" : "closed"}
        ref={ref}
        {...props}
      />
    );
  },
);

interface DialogContentProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}

const QDialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  (props, ref) => {
    const nodeRef = useRef<HTMLDivElement>();
    const hideOthersRef = useRef<Undo>();
    const {
      open,
      onOpenChange,
      onContentNodeChange,
      destroyDialogWhenEscapePress,
      onEscapeKeyDown,
    } = useDialogRootContext();

    useEffect(() => {
      if (nodeRef.current && open) {
        hideOthersRef.current = hideOthers(nodeRef.current);
      }

      return () => hideOthersRef.current?.();
    }, [open]);

    useEffect(() => {
      const listener = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          if (destroyDialogWhenEscapePress) {
            onOpenChange(false);
          } else {
            onEscapeKeyDown?.();
          }
        }
      };

      document?.addEventListener("keydown", listener);

      if (!open) {
        document?.removeEventListener("keydown", listener);
      }

      return () => document?.removeEventListener("keydown", listener);
    }, [open, onOpenChange, destroyDialogWhenEscapePress, onEscapeKeyDown]);

    if (!open) return null;

    return (
      <QFocusTrap>
        <div
          role="dialog"
          data-state={open ? "open" : "closed"}
          aria-modal={open}
          ref={(node) => {
            if (!node) return;

            onContentNodeChange(node);
            nodeRef.current = node;

            if (ref) {
              if (typeof ref === "function") {
                ref(node);
              } else {
                ref.current = node;
              }
            }
          }}
          {...props}
        />
      </QFocusTrap>
    );
  },
);

interface DialogTitleProps
  extends DetailedHTMLProps<
    HTMLAttributes<HTMLHeadingElement>,
    HTMLHeadingElement
  > {}

const QDialogTitle = forwardRef<HTMLHeadingElement, DialogTitleProps>(
  (props, ref) => {
    return (
      <h1 ref={ref} {...props}>
        {props.children}
      </h1>
    );
  },
);

interface DialogDescriptionProps
  extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {}

const QDialogDescription = forwardRef<HTMLDivElement, DialogDescriptionProps>(
  (props, ref) => {
    return <div ref={ref} {...props} />;
  },
);

interface DialogCloseButtonProps
  extends DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {}

const QDialogCloseButton = forwardRef<
  HTMLButtonElement,
  DialogCloseButtonProps
>((props, ref) => {
  const context = useDialogRootContext();
  return (
    <button
      type="button"
      ref={ref}
      {...props}
      onClick={(e) => {
        context.onOpenChange(false);
        props.onClick?.(e);
      }}
    />
  );
});

const Root = QDialogRoot;
const Trigger = QDialogTrigger;
const Portal = QDialogPortal;
const Overlay = QDialogOverlay;
const Content = QDialogContent;
const Title = QDialogTitle;
const CloseButton = QDialogCloseButton;
const Description = QDialogDescription;

export {
  QDialogRoot,
  QDialogTrigger,
  QDialogPortal,
  QDialogOverlay,
  QDialogContent,
  QDialogTitle,
  QDialogCloseButton,
  QDialogDescription,
  //
  //
  //
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  CloseButton,
  Description,
};
